import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Dashboard from './pages/Dashboard';
import ProjectDashboard from './pages/ProjectDashboard';
import Projects from './pages/Projects';
import ProjectDetails from './pages/ProjectDetails';
import Activities from './pages/Activities';
import Reports from './pages/Reports';
import Templates from './pages/Templates';
import Evaluations from './pages/Evaluations';
import Team from './pages/Team';
import Settings from './pages/Settings';
import Login from './pages/Login';
import PublicEvaluation from './pages/PublicEvaluation';
import { login as apiLogin, logout as apiLogout, ensureLogin, getCurrentUser } from './utils/api';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import './styles/App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const location = useLocation();
  const theme = useMemo(() => {
    const prefs = currentUser?.preferences || {};
    const primaryColor = prefs.themeColor || '#1976d2';
    const compact = Boolean(prefs.compact);
    const darkMode = Boolean(prefs.darkMode);
    return createTheme({
      palette: {
        mode: darkMode ? 'dark' : 'light',
        primary: { main: primaryColor },
        secondary: { main: '#f50057' },
        background: { default: darkMode ? '#121212' : '#f5f5f5' }
      },
      components: {
        MuiButton: {
          styleOverrides: { root: { borderRadius: 8, padding: compact ? '4px 10px' : undefined } }
        },
        MuiCard: {
          styleOverrides: { root: { borderRadius: 12, boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)' } }
        },
        MuiTable: {
          defaultProps: { size: compact ? 'small' : 'medium' }
        },
        MuiListItem: {
          defaultProps: { dense: compact }
        }
      }
    });
  }, [currentUser]);

  // Autenticação real via API
  const handleLogin = async (credentials) => {
    try {
      await apiLogin(credentials);
      setIsAuthenticated(true);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleLogout = () => {
    apiLogout();
    setIsAuthenticated(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Tenta autenticar automaticamente ao abrir a app
  useEffect(() => {
    (async () => {
      try {
        if (location.pathname.startsWith('/public/evaluation/')) {
          setIsAuthenticated(false);
          return;
        }
        const tok = await ensureLogin();
        if (tok) {
          setIsAuthenticated(true);
          const me = await getCurrentUser();
          setCurrentUser(me);
        } else {
          setIsAuthenticated(false);
        }
      } catch (e) {
        setIsAuthenticated(false);
      }
    })();
  }, [location.pathname]);

  // Rota pública de avaliação SEM layout da aplicação (independente de autenticação)
  if (location.pathname.startsWith('/public/evaluation/')) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* Monta rotas públicas para fornecer params ao componente */}
        <Routes>
          <Route path="/public/evaluation/by-id/:id" element={<PublicEvaluation />} />
          <Route path="/public/evaluation/:token" element={<PublicEvaluation />} />
        </Routes>
      </ThemeProvider>
    );
  }
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
        <Navbar 
          onMenuToggle={toggleSidebar} 
          onLogout={handleLogout} 
          user={currentUser} 
          onUpdatePreferences={(prefs) => setCurrentUser(prev => ({ ...prev, preferences: { ...prev?.preferences, ...prefs } }))}
          onUserChange={(next) => setCurrentUser(prev => ({ ...prev, ...next }))}
        />
        <Sidebar open={sidebarOpen} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 2,
            mt: 8,
            pt: '60px',
            // Espaçamento horizontal confortável (~20px) sem criar espaço em branco entre a Sidebar e o conteúdo
            px: 2.5,
            transition: 'padding 0.2s',
            backgroundColor: 'background.default',
            minHeight: 'calc(100vh - 64px)',
            overflow: 'auto'
          }}
        >
            <Routes>
              <Route path="/public/evaluation/by-id/:id" element={<PublicEvaluation />} />
              <Route path="/public/evaluation/:token" element={<PublicEvaluation />} />
              <Route path="/" element={<Dashboard />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetails />} />
              <Route path="/projects/:id/dashboard" element={<ProjectDashboard />} />
              <Route path="/activities" element={<Activities />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/evaluations" element={<Evaluations />} />
              <Route path="/team" element={<Team />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
